import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { DATENSCHUTZ } from "@/constants/testIds/landing";
import { useSiteContent } from "@/lib/siteContent";

const ph = (v, fallback) => (v && String(v).trim() ? v : fallback);

export default function Datenschutz() {
  const { content } = useSiteContent();
  const imp = content.impressum || {};
  const email = ph(imp.email || content.contact_email, "admin@meieiko.com");

  return (
    <div
      data-testid={DATENSCHUTZ.page}
      className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]"
    >
      <Header onCtaClick={() => {}} />

      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid={DATENSCHUTZ.backToHome}
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#6B8E23] transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>

          <h1 className="font-[Outfit] text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Datenschutzerklärung
          </h1>
          <p className="text-[#666]">
            Stand: {new Date().toLocaleDateString("de-DE")}
          </p>

          <article className="prose-meiei mt-10">
            <p>
              Diese Datenschutzerklärung informiert dich gemäß Art. 13
              Datenschutz-Grundverordnung (DSGVO) über die Verarbeitung
              personenbezogener Daten bei der Nutzung der Anwendung{" "}
              <strong>Waskowi</strong> (nachfolgend &bdquo;App&ldquo;).
            </p>

            {/* 1 */}
            <h2>1. Verantwortlicher</h2>
            <p>Verantwortlicher im Sinne des Art. 4 Nr. 7 DSGVO ist:</p>
            <p>
              {ph(imp.name, "[Name des Verantwortlichen]")}
              <br />
              {ph(imp.street, "[Straße und Hausnummer]")}
              <br />
              {ph(imp.postal_city, "[PLZ und Ort]")}
              <br />
              {ph(imp.country, "Deutschland")}
            </p>
            <p>
              E-Mail: <a href={`mailto:${email}`}>{email}</a>
              {imp.phone && (
                <>
                  <br />
                  Telefon: {imp.phone}
                </>
              )}
            </p>

            {/* 2 */}
            <h2>2. Welche Daten wir verarbeiten</h2>
            <p>
              Bei der Nutzung von Waskowi verarbeiten wir folgende
              personenbezogene Daten:
            </p>
            <p>
              <strong>a) Konto- und Login-Daten</strong>
            </p>
            <ul>
              <li>E-Mail-Adresse (dient zugleich als Benutzername)</li>
              <li>Passwort (nur als sicherer bcrypt-Hash, niemals im Klartext)</li>
              <li>Optional: Anzeigename</li>
              <li>Zeitpunkt der Registrierung, letzter Login</li>
              <li>
                Zeitpunkt der letzten Passwort-Änderung (<code>pwd_changed_at</code>)
                zur Token-Invalidierung
              </li>
            </ul>
            <p>
              <strong>b) Nutzungsdaten</strong>
            </p>
            <ul>
              <li>
                Selbst angelegte Gerichte (Name, Beschreibung, Zutaten,
                Zubereitung, Portionen, Küche)
              </li>
              <li>Zugehörige Fotos (als Base64-Blob in MongoDB gespeichert)</li>
              <li>Einkaufslisten und deren Status (abgehakte Positionen, Notizen)</li>
              <li>
                Notizen inkl. optionaler Sprachaufnahmen (temporär zur
                Transkription; siehe 4b)
              </li>
              <li>Chatverlauf mit dem Koch-Assistenten</li>
              <li>Onboarding-Antworten (Ernährungsvorlieben, Küchen, Ausschlüsse)</li>
              <li>Sprach-Einstellung (de / en)</li>
            </ul>
            <p>
              <strong>c) Technische Daten</strong>
            </p>
            <ul>
              <li>
                IP-Adresse (zur Auslieferung der Antworten und für Rate-Limiting;
                nicht dauerhaft gespeichert)
              </li>
              <li>Zeitstempel der API-Aufrufe (Server-Logs, i. d. R. 14 Tage)</li>
              <li>Nutzeragent (App-Version, Plattform iOS/Android/Web)</li>
            </ul>
            <p>
              <strong>d) Feedback</strong>
            </p>
            <ul>
              <li>
                Wenn du das Feedback-Formular nutzt: Feedback-Text und
                E-Mail-Zeitpunkt
              </li>
            </ul>
            <p>
              <strong>e) Geräteberechtigungen (Kamera &amp; Mikrofon)</strong>
            </p>
            <ul>
              <li>
                <strong>Kamera</strong>: Der Zugriff erfolgt ausschließlich, wenn
                du ein Rezept aus einem Kochbuch per Foto einscannst
                (KI-Rezept-Scan). Das aufgenommene Bild wird zur Texterkennung an
                den KI-Dienst (siehe 4b) übermittelt und nicht dauerhaft
                gespeichert, sofern du das Rezept nicht selbst speicherst.
              </li>
              <li>
                <strong>Mikrofon</strong>: Der Zugriff erfolgt ausschließlich,
                wenn du mit dem Koch-Assistenten sprichst oder eine Sprachnotiz
                erstellst. Die Aufnahme wird zur Transkription (Sprach-zu-Text)
                verarbeitet und danach automatisch gelöscht (siehe 4b und 5).
              </li>
            </ul>
            <p>
              Beide Berechtigungen werden erst nach deiner ausdrücklichen Freigabe
              und nur für die jeweilige Funktion genutzt. Du kannst die
              Berechtigungen jederzeit in den Systemeinstellungen deines Geräts
              widerrufen; die betreffende Funktion steht dann ggf. nicht mehr zur
              Verfügung. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO
              (Bereitstellung der von dir angeforderten Funktion).
            </p>

            {/* 3 */}
            <h2>3. Zwecke und Rechtsgrundlagen der Verarbeitung</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse my-4">
                <thead>
                  <tr className="text-left border-b border-[#DDD]">
                    <th className="py-2 pr-4 font-semibold">Zweck</th>
                    <th className="py-2 pr-4 font-semibold">Daten</th>
                    <th className="py-2 font-semibold">Rechtsgrundlage</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <tr className="border-b border-[#EEE]">
                    <td className="py-2 pr-4">Bereitstellung des Nutzerkontos und Login</td>
                    <td className="py-2 pr-4">Konto- und Login-Daten</td>
                    <td className="py-2">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</td>
                  </tr>
                  <tr className="border-b border-[#EEE]">
                    <td className="py-2 pr-4">Speichern und Anzeigen deiner Gerichte, Listen, Notizen</td>
                    <td className="py-2 pr-4">Nutzungsdaten</td>
                    <td className="py-2">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                  <tr className="border-b border-[#EEE]">
                    <td className="py-2 pr-4">KI-Funktionen (Rezept-Vorschläge, Zutatenerkennung, Sprach-zu-Text, Bilder)</td>
                    <td className="py-2 pr-4">Nutzereingaben, Bilder, Sprachaufnahmen</td>
                    <td className="py-2">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                  <tr className="border-b border-[#EEE]">
                    <td className="py-2 pr-4">Rate-Limiting, Sicherheit, Abwehr von Missbrauch</td>
                    <td className="py-2 pr-4">IP-Adresse, Logs</td>
                    <td className="py-2">Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Passwort-Reset per E-Mail</td>
                    <td className="py-2 pr-4">E-Mail-Adresse</td>
                    <td className="py-2">Art. 6 Abs. 1 lit. b DSGVO</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 4 */}
            <h2>4. Verarbeitung durch Auftragsverarbeiter / Drittdienste</h2>
            <p>
              Zur Bereitstellung der App nutzen wir folgende Dienste. Mit allen
              Anbietern bestehen (bzw. werden bestehen) Auftragsverarbeitungs­verträge
              nach Art. 28 DSGVO oder es liegen geeignete Garantien nach Art. 46
              DSGVO vor:
            </p>
            <p>
              <strong>a) Hosting</strong>
              <br />
              Anbieter: Emergent Platform (Kubernetes-Cluster)
              <br />
              Standort der Server: USA – abgesichert über
              EU-Standardvertragsklauseln (SCC)
              <br />
              Verarbeitete Daten: Alle App-Daten, Server-Logs
              <br />
              Bei Verarbeitung in Drittländern (z. B. USA) stützen wir uns auf
              EU-Standardvertragsklauseln (SCC) und ggf. das EU-U.S. Data Privacy
              Framework.
            </p>
            <p>
              <strong>b) KI-Dienste (LLM-Anbieter)</strong>
              <br />
              Für die generativen Funktionen (Gericht-Erstellung aus Text,
              Rezept-Scan aus Foto, Chat-Assistent, Sprach-zu-Text,
              Bild-Generierung) werden deine Eingaben an folgende Anbieter
              übermittelt:
            </p>
            <ul>
              <li>
                <strong>OpenAI, Inc.</strong>, 3180 18th St, San Francisco, CA
                94110, USA — für GPT-Text-Modelle, Whisper (Sprach-zu-Text),
                GPT-Image-1, Text-to-Speech
              </li>
              <li>
                <strong>Anthropic PBC</strong>, 548 Market St PMB 90375, San
                Francisco, CA 94104, USA — für Claude-Text-Modelle
              </li>
              <li>
                <strong>Google LLC</strong>, 1600 Amphitheatre Parkway, Mountain
                View, CA 94043, USA — für Gemini-Bild- und Text-Modelle
              </li>
            </ul>
            <p>
              Die Übermittlung erfolgt in die USA. Rechtsgrundlage:
              EU-Standardvertragsklauseln bzw. EU-U.S. Data Privacy Framework
              (Adequacy Decision 2023). Sprachaufnahmen werden nach der
              Transkription automatisch gelöscht und nicht dauerhaft gespeichert.
            </p>
            <p>
              <strong>c) E-Mail-Versand</strong>
              <br />
              Anbieter: <strong>Resend, Inc.</strong>, 2261 Market Street #4933,
              San Francisco, CA 94114, USA
              <br />
              Zweck: Versand von Passwort-Reset-E-Mails
              <br />
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO
              <br />
              Übermittlung in die USA basierend auf EU-Standardvertragsklauseln.
            </p>
            <p>
              <strong>d) Datenbank</strong>
              <br />
              Anbieter: MongoDB
              <br />
              Standort: innerhalb der Emergent-Infrastruktur (siehe 4a)
              <br />
              Zweck: dauerhafte Speicherung aller App-Daten
            </p>

            {/* 5 */}
            <h2>5. Speicherdauer</h2>
            <ul>
              <li>
                <strong>Konto-Daten</strong>: solange dein Konto besteht + 30 Tage
                nach Löschanfrage (zur endgültigen Backup-Bereinigung)
              </li>
              <li>
                <strong>Gerichte, Listen, Notizen, Fotos</strong>: solange dein
                Konto besteht bzw. bis du sie manuell löschst
              </li>
              <li>
                <strong>Server-Logs (IP-Adresse, Timestamps)</strong>: 14 Tage,
                danach automatische Löschung
              </li>
              <li>
                <strong>Passwort-Reset-Tokens</strong>: 1 Stunde ab Erstellung
              </li>
              <li>
                <strong>Sprachaufnahmen</strong>: nur während der Transkription
                (typischerweise &lt; 30 Sekunden)
              </li>
              <li>
                <strong>Feedback-Nachrichten</strong>: dauerhaft, bis du eine
                Löschung anforderst
              </li>
            </ul>

            {/* 6 */}
            <h2>6. Deine Rechte als betroffene Person</h2>
            <p>Du hast folgende Rechte gegenüber uns:</p>
            <ul>
              <li>
                <strong>Auskunft</strong> (Art. 15 DSGVO): Bestätigung, ob wir
                Daten von dir verarbeiten, sowie Kopie dieser Daten
              </li>
              <li>
                <strong>Berichtigung</strong> (Art. 16 DSGVO): Korrektur falscher
                Daten
              </li>
              <li>
                <strong>Löschung</strong> (&bdquo;Recht auf Vergessenwerden&ldquo;,
                Art. 17 DSGVO): Löschung aller Daten, sofern keine gesetzlichen
                Aufbewahrungspflichten entgegenstehen
              </li>
              <li>
                <strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)
              </li>
              <li>
                <strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO): Erhalt
                deiner Daten in einem strukturierten, maschinenlesbaren Format
              </li>
              <li>
                <strong>Widerspruch</strong> gegen die Verarbeitung (Art. 21
                DSGVO), soweit sie auf berechtigtem Interesse basiert
              </li>
              <li>
                <strong>Widerruf einer erteilten Einwilligung</strong> jederzeit
                für die Zukunft
              </li>
              <li>
                <strong>Beschwerderecht bei einer Aufsichtsbehörde</strong> (Art.
                77 DSGVO). Zuständig ist deine Landesdatenschutzbehörde am Wohnort
                oder für {ph(imp.country, "Deutschland")}: Unabhängiges
                Datenschutzzentrum Saarland – Die Landesbeauftragte für
                Datenschutz und Informationsfreiheit, Fritz-Dobisch-Straße 12,
                66111 Saarbrücken
              </li>
            </ul>
            <p>
              <strong>Kontoauskunft &amp; Löschung in der App</strong>: Du kannst
              dein Konto direkt in den Profil-Einstellungen der App löschen. Die
              vollständige Löschung inkl. aller Daten erfolgt innerhalb von 30
              Tagen. Alternativ kannst du eine Löschung über die Seite{" "}
              <Link to="/konto-loeschen">Konto &amp; Daten löschen</Link>{" "}
              beantragen.
            </p>

            {/* 7 */}
            <h2>7. Datensicherheit</h2>
            <p>
              Wir setzen technische und organisatorische Maßnahmen (TOM) gemäß
              Art. 32 DSGVO um:
            </p>
            <ul>
              <li>
                <strong>Transportverschlüsselung</strong> aller Verbindungen per
                TLS 1.3
              </li>
              <li>
                <strong>Passwort-Hashing</strong> mit bcrypt und individuellem Salt
              </li>
              <li>
                <strong>Session-Tokens</strong> (JWT) mit begrenzter Laufzeit (30
                Tage) und automatischer Invalidierung bei Passwort-Änderung
              </li>
              <li>
                <strong>Zugriffsbeschränkung</strong> auf Server nur über
                SSH-Keys, keine Passwort-Authentifizierung
              </li>
              <li>
                <strong>Backups</strong> verschlüsselt gespeichert
              </li>
              <li>
                <strong>Prinzip der Datenminimierung</strong>: wir erheben nur die
                Daten, die für den Betrieb der App erforderlich sind
              </li>
              <li>
                <strong>Rate-Limiting</strong> gegen Brute-Force-Angriffe auf Login
                und Passwort-Reset
              </li>
            </ul>

            {/* 8 */}
            <h2>8. Cookies und lokaler Speicher</h2>
            <p>
              Die App verwendet keine Tracking-Cookies und kein Analytics. Auf
              deinem Gerät werden folgende Daten lokal gespeichert:
            </p>
            <ul>
              <li>
                <strong>JWT-Token</strong> (in Expo SecureStore / AsyncStorage) —
                zur Aufrechterhaltung der Sitzung
              </li>
              <li>
                <strong>Gewählte Portionen pro Gericht</strong> (in AsyncStorage) —
                zur Wiederherstellung deiner letzten Auswahl
              </li>
              <li>
                <strong>Sprach-Einstellung</strong> — zur Anzeige der
                App-Oberfläche in deiner bevorzugten Sprache
              </li>
              <li>
                <strong>Offline-Queue</strong> — für Aktionen, die während
                fehlender Internetverbindung durchgeführt wurden
              </li>
            </ul>
            <p>
              Diese Daten verlassen dein Gerät nur, wenn du eine authentifizierte
              Anfrage an unseren Server sendest.
            </p>

            {/* 9 */}
            <h2>9. Automatisierte Entscheidungsfindung / Profiling</h2>
            <p>
              Es findet keine automatisierte Entscheidungsfindung im Sinne von
              Art. 22 DSGVO statt. Die KI-generierten Rezept-Vorschläge sind reine
              Assistenzfunktionen ohne rechtliche Wirkung.
            </p>

            {/* 10 */}
            <h2>10. Altersbeschränkung</h2>
            <p>
              Die App unterliegt keiner Altersbeschränkung und kann grundsätzlich
              von Personen jeden Alters genutzt werden. Bei der Nutzung durch
              Minderjährige empfehlen wir, dass diese im Einvernehmen mit den
              Sorgeberechtigten erfolgt.
            </p>

            {/* 11 */}
            <h2>11. Änderungen dieser Datenschutzerklärung</h2>
            <p>
              Diese Datenschutzerklärung kann von Zeit zu Zeit angepasst werden,
              um Änderungen der Rechtslage oder der App-Funktionen widerzuspiegeln.
              Die jeweils aktuelle Version ist in der App unter &bdquo;Profil →
              Datenschutz&ldquo; verfügbar.
            </p>

            {/* 12 */}
            <h2>12. Kontakt zum Datenschutz</h2>
            <p>
              Für alle Fragen zum Datenschutz oder zur Ausübung deiner Rechte
              erreichst du uns unter:
            </p>
            <p>
              E-Mail: <a href={`mailto:${email}`}>{email}</a>
              <br />
              Post: siehe Ziffer 1
            </p>
            <p>
              Wir antworten in der Regel innerhalb von 30 Tagen (Art. 12 Abs. 3
              DSGVO).
            </p>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
